import React, { useCallback, useEffect, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router';
import { Box, Typography, useTheme } from '@mui/material';
import { IconProp } from '@fortawesome/fontawesome-svg-core';

import {
    ContextActionType,
    EmptyBox,
    HeaderActionButton,
    HeaderListSkeleton,
    ListHeader,
    ListSkeleton,
    SkeletonLoader,
    useDumbActionsContext,
    useModal,
} from '@europrocurement/flexy-components';
import {
    ColumnDatatable,
    FiltersDatatableList,
    StoreDatatable,
} from '@europrocurement/flexy-datatable';
import {
    groupesTravailSelector,
    GroupeTravailUtilisateurApiObject,
    useUtilisateurService,
    useUtilisateursServiceSelector,
    UtilisateursAppDispatch,
} from '@europrocurement/l2d-domain';
import { FaOptionIcon, plusIcon } from '@europrocurement/l2d-icons';

import { useInitialLoadingState } from '@europrocurement/l2d-hooks';
import { utilisateursUtilisateursPath } from '../../constants/paths';
import GroupesTravailActionMenu from '../../components/molecule/GroupesTravailActionMenu';
import { WorkGroupCreateForm } from '../../components/GroupesTravail/WorkGroupModals/WorkGroupCreateForm';

const filters: FiltersDatatableList = [
    {
        label: 'Libellé',
        field: 'lib',
        type: 'text',
    },
    {
        label: 'Domaine',
        field: 'domaineDdmLibelle',
        type: 'text',
    },
    {
        label: 'Groupes cloturés',
        field: 'fDeleteStatus',
        type: 'boolean',
    },
];

/**
 * Page de listing des groupes de travail.
 */
const GroupesTravailList: React.FunctionComponent = function () {
    const theme = useTheme();
    const navigate = useNavigate();
    const dispatch = useDispatch<UtilisateursAppDispatch>();
    const { modalActions } = useModal();

    const { getGroupesTravail } = useUtilisateurService();
    const dataSource = useUtilisateursServiceSelector(groupesTravailSelector);
    const { isInitialLoading, isInError } = useInitialLoadingState(dataSource);

    const breadcrumbPath = useMemo(
        () => [
            {
                link: '/accueil',
                icon: {
                    icon: ['fasl', 'house'] as IconProp,
                    sx: { display: 'flex', alignItems: 'center' },
                },
            },
            { link: '/utilisateurs', path: 'Gestion des utilisateurs' },
            'Groupes de travail',
        ],
        [],
    );

    const columns: ColumnDatatable<GroupeTravailUtilisateurApiObject>[] = useMemo(
        () => [
            {
                label: 'Libellé',
                render: 'lib',
                isDisplayed: true,
                sortable: true,
                field: 'lib',
            },
            {
                label: 'Référent',
                render: (workgroup: GroupeTravailUtilisateurApiObject) => (
                    <Typography
                        variant="l2dcaption"
                        sx={{
                            ':hover': { cursor: 'pointer', color: theme.palette.primary.dark },
                        }}
                    >
                        {workgroup.iduserReferent?.prenom} {workgroup.iduserReferent?.nom}
                    </Typography>
                ),
                isDisplayed: true,
                sortable: false,
                field: 'iduserReferent',
                onClickCell(e, workgroup: GroupeTravailUtilisateurApiObject) {
                    navigate(`${utilisateursUtilisateursPath}${workgroup.iduserReferent?.id}`);
                },
            },
            {
                label: 'Domaine',
                render: (workgroup: GroupeTravailUtilisateurApiObject) =>
                    workgroup.domaine?.ddmLibelle,
                isDisplayed: true,
                sortable: true,
                field: 'domaineDdmLibelle',
            },
            {
                label: 'Mail',
                render: 'email',
                isDisplayed: true,
                sortable: false,
                field: 'email',
            },
            {
                label: 'Téléphone',
                render: 'phone',
                isDisplayed: true,
                sortable: false,
                field: 'phone',
            },
            {
                label: 'Dates',
                render: (workgroup: GroupeTravailUtilisateurApiObject) => {
                    const creation = workgroup.dCreation
                        ? new Date(workgroup.dCreation).toLocaleDateString()
                        : 'Non spécifié';
                    const cloture = workgroup.dCloture
                        ? new Date(workgroup.dCloture).toLocaleDateString()
                        : 'Non spécifié';
                    return `${creation} - ${cloture}`;
                },
                isDisplayed: true,
                sortable: true,
                field: 'dCreation',
            },
            {
                label: 'Actions',
                render: (workgroup: GroupeTravailUtilisateurApiObject) =>
                    workgroup.id &&
                    workgroup.iduserReferent?.id && (
                        <GroupesTravailActionMenu
                            workgroupId={workgroup.id}
                            referentId={workgroup.iduserReferent.id}
                        />
                    ),
                isDisplayed: true,
                displayLabel: false,
                rowReverse: true,
            },
        ],
        [navigate, theme.palette.primary.dark],
    );

    const handleCreateWorkGroup = useCallback(() => {
        modalActions.call(<WorkGroupCreateForm />);
    }, [modalActions]);

    // Chargement initial des données
    useEffect(() => {
        dispatch(getGroupesTravail({}));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleRowClick = useCallback(
        (_: React.MouseEvent<HTMLElement>, workgroup: GroupeTravailUtilisateurApiObject) => {
            navigate(`${workgroup.id}`);
        },
        [navigate],
    );

    const contextActions: ContextActionType[] = useMemo(
        () => [
            {
                name: 'Nouveau groupe de travail',
                icon: <FaOptionIcon {...plusIcon.props} />,
                action: handleCreateWorkGroup,
            },
        ],
        [handleCreateWorkGroup],
    );

    useDumbActionsContext(contextActions);

    const errorContent = useMemo(
        () => (
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    height: '100vh',
                    width: '100%',
                }}
            >
                <EmptyBox
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'space-evenly',
                        height: '25%',
                        width: '50%',
                    }}
                    text="Une erreur s'est produite lors de la récupération des groupes de travail."
                    subText="Veuillez recharger la page. Si le problème persiste, contactez votre administrateur."
                />
            </Box>
        ),
        [],
    );

    return (
        <SkeletonLoader
            isLoading={isInitialLoading}
            skeleton={
                <>
                    <HeaderListSkeleton />
                    <ListSkeleton
                        showSearch
                        columns={7}
                    />
                </>
            }
            isInError={isInError}
            errorChildren={errorContent}
        >
            <ListHeader
                breadcrumbPath={breadcrumbPath}
                actions={
                    <HeaderActionButton
                        label="Nouveau groupe de travail"
                        icon={plusIcon}
                        onClick={handleCreateWorkGroup}
                        actionType="modal"
                    />
                }
            />

            {dataSource && (
                <StoreDatatable
                    dataSource={dataSource}
                    columns={columns}
                    fetchData={getGroupesTravail}
                    filters={filters}
                    showSearch
                    localStorageKey="FlexyStoreDatatable"
                    onClickRow={handleRowClick}
                />
            )}
        </SkeletonLoader>
    );
};

export default GroupesTravailList;
